/**
 * Platform Notifications — internal operator alert queue
 */
import React, { useState, useCallback } from "react";
import { notifications as notifApi } from "../api.js";
import { useAsync } from "../hooks.js";
import type { NotificationSeverity } from "@bizzness/shared-types";

function severityBadge(severity: NotificationSeverity) {
  const map: Record<NotificationSeverity, string> = {
    info: "ops-badge-blue", warning: "ops-badge-amber",
    error: "ops-badge-red", critical: "ops-badge-red",
  };
  return <span className={`ops-badge ${map[severity]}`}>{severity}</span>;
}

export function NotificationsView() {
  const [severity, setSeverity]   = useState("");
  const [unreadOnly, setUnread]   = useState(false);
  const [page, setPage]           = useState(1);
  const [busy, setBusy]           = useState<string | null>(null);

  const fetcher = useCallback(
    () => notifApi.list({
      ...(severity   ? { severity }         : {}),
      ...(unreadOnly ? { unread: "true" }   : {}),
      page: String(page), pageSize: "50",
    }),
    [severity, unreadOnly, page]
  );
  const { data, loading, error, reload } = useAsync<any>(fetcher, [severity, unreadOnly, page]);

  async function markRead(id: string) {
    setBusy(id);
    await notifApi.markRead(id).catch(() => {});
    setBusy(null); reload();
  }

  async function dismiss(id: string) {
    setBusy(id);
    await notifApi.dismiss(id).catch(() => {});
    setBusy(null); reload();
  }

  const rows: any[] = data?.items ?? [];

  return (
    <div className="ops-page">
      <div className="ops-page-header">
        <div>
          <h1 className="ops-page-title">Notifications</h1>
          {data && <p className="ops-page-subtitle">{data.total} total</p>}
        </div>
        <button className="ops-btn ops-btn-secondary ops-btn-sm" onClick={reload}>↻ Refresh</button>
      </div>

      {error && <div className="ops-error">{error}</div>}

      <div className="ops-filters">
        <select className="ops-select" value={severity}
          onChange={e => { setSeverity(e.target.value); setPage(1); }}>
          <option value="">All severities</option>
          {["info","warning","error","critical"].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: "0.375rem",
          fontSize: "0.8125rem", color: "var(--ops-muted)", cursor: "pointer" }}>
          <input type="checkbox" checked={unreadOnly}
            onChange={e => { setUnread(e.target.checked); setPage(1); }} />
          Unread only
        </label>
      </div>

      {loading && <div className="ops-loading">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="ops-empty">
          {unreadOnly ? "No unread notifications." : "No notifications found."}
        </div>
      )}

      {rows.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {rows.map((n: any) => (
            <div key={n.id} className="ops-card"
              style={{ borderLeft: `3px solid ${n.severity === "critical" || n.severity === "error"
                ? "var(--ops-danger)" : n.severity === "warning"
                ? "var(--ops-warning)" : "var(--ops-info)"}`,
                opacity: n.readAt ? 0.65 : 1 }}>
              <div className="ops-row" style={{ marginBottom: "0.375rem" }}>
                {severityBadge(n.severity)}
                <span style={{ fontWeight: 600, fontSize: "0.875rem" }}>{n.title}</span>
                {!n.readAt && (
                  <span className="ops-badge ops-badge-blue" style={{ fontSize: "0.65rem" }}>Unread</span>
                )}
                <span className="ops-spacer" />
                <span style={{ fontSize: "0.7rem", color: "var(--ops-muted)" }}>
                  {n.createdAt?.slice(0, 16).replace("T", " ")}
                </span>
              </div>
              <div style={{ fontSize: "0.8125rem", color: "var(--ops-muted)", marginBottom: "0.5rem" }}>
                {n.message}
              </div>
              {n.tenantId && (
                <div style={{ fontSize: "0.75rem", color: "var(--ops-muted)" }}>
                  Tenant: <code>{n.tenantId}</code>
                </div>
              )}
              <div style={{ display: "flex", gap: "0.375rem", marginTop: "0.5rem" }}>
                {!n.readAt && (
                  <button className="ops-btn ops-btn-sm ops-btn-secondary"
                    disabled={busy === n.id} onClick={() => markRead(n.id)}>
                    Mark read
                  </button>
                )}
                <button className="ops-btn ops-btn-sm ops-btn-secondary"
                  disabled={busy === n.id} onClick={() => dismiss(n.id)}>
                  Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {data?.hasMore && (
        <div className="ops-pagination">
          {page > 1 && <button className="ops-btn ops-btn-sm ops-btn-secondary"
            onClick={() => setPage(p => p - 1)}>← Prev</button>}
          <span>Page {page}</span>
          <button className="ops-btn ops-btn-sm ops-btn-secondary"
            onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
}
